// PB admin console for the EventConnect conference platform.
// Six tabs: Events · Create/Edit · Registrations & Selection · Scheduling · Live · Analytics.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell,
} from 'recharts';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import {
  C, EventRow, EventStatus, PlenarySession, Speaker, Registration, ManagerSlot, Round,
  ManagerAvailability, InvestorPreference, InvestorSchedule, NPMMeeting, EventNotification,
  SESSION_TYPES, TARGET_AUDIENCES, TargetAudience, SessionType,
  fmtDate, fmtDateRange, fmtTime, fmtTimeRange, toLocalInput,
  runAISlotBuilder, runAIScheduleOptimisation, suggestRooms,
  notifyManagersSelected, notifyPreferencesOpen, notifySchedulePublished, sendEventNotification,
  toCSV, downloadCSV, aumLabel,
} from '../lib/event';
import {
  card, inputStyle, labelStyle, btnPrimary, btnGold, btnGhost, btnDanger,
  PageHeader, SectionLabel, StatusBadge, Banner, LoadingScreen, EmptyState, Pill,
  StatCard, Modal, ProgressBar, StepTabs,
} from '../lib/eventUI';

const AGENDA_BUCKET = 'event-assets';

type AdminTab = 'events' | 'create' | 'registrations' | 'scheduling' | 'live' | 'analytics';

const ADMIN_TABS: { id: AdminTab; label: string }[] = [
  { id: 'events', label: 'Events' },
  { id: 'create', label: 'Create / Edit' },
  { id: 'registrations', label: 'Registrations & Selection' },
  { id: 'scheduling', label: 'Scheduling' },
  { id: 'live', label: 'Live Event' },
  { id: 'analytics', label: 'Analytics' },
];

export default function PBEventAdmin() {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const [tab, setTab] = useState<AdminTab>('events');
  const [events, setEvents] = useState<EventRow[]>([]);
  const [regCounts, setRegCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('events').select('*').order('start_date', { ascending: false });
    const evs = (data || []) as EventRow[];
    setEvents(evs);
    // registration counts
    const counts: Record<string, number> = {};
    await Promise.all(evs.map(async ev => {
      const { count } = await supabase.from('event_registrations')
        .select('*', { count: 'exact', head: true }).eq('event_id', ev.id);
      counts[ev.id] = count || 0;
    }));
    setRegCounts(counts);
    if (!activeEventId && evs.length) setActiveEventId(evs[0].id);
    setLoading(false);
  }, [activeEventId]);

  useEffect(() => { if (profile?.is_super_admin) loadEvents(); }, [profile, loadEvents]);

  if (profileLoading) return <LoadingScreen />;
  if (!profile?.is_super_admin) {
    return (
      <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui' }}>
        <Banner type="err" text="This area is restricted to the Altveno admin team." />
      </div>
    );
  }

  const activeEvent = events.find(e => e.id === activeEventId) || null;

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <PageHeader title="Event Admin" subtitle="EventConnect, conference management console" />

      <StepTabs<AdminTab>
        tabs={ADMIN_TABS}
        active={tab}
        onChange={setTab}
      />

      {loading ? <LoadingScreen /> : (
        <>
          {tab === 'events' && (
            <EventsTab
              events={events} regCounts={regCounts}
              onCreate={() => { setEditingId(null); setTab('create'); }}
              onManage={(id) => { setActiveEventId(id); setTab('scheduling'); }}
              onEdit={(id) => { setEditingId(id); setTab('create'); }}
            />
          )}
          {tab === 'create' && (
            <CreateEditTab
              userId={user?.id || null}
              event={editingId ? events.find(e => e.id === editingId) || null : null}
              onSaved={async (id) => { await loadEvents(); setActiveEventId(id); setEditingId(id); }}
              onClose={() => setTab('events')}
            />
          )}
          {tab !== 'events' && tab !== 'create' && (
            <EventSelector events={events} value={activeEventId} onChange={setActiveEventId} />
          )}
          {tab === 'registrations' && activeEvent && (
            <RegistrationsTab event={activeEvent} userId={user?.id || null} onReload={loadEvents} />
          )}
          {tab === 'scheduling' && activeEvent && (
            <SchedulingTab event={activeEvent} userId={user?.id || null} onEventChanged={loadEvents} />
          )}
          {tab === 'live' && activeEvent && (
            <LiveTab event={activeEvent} userId={user?.id || null} />
          )}
          {tab === 'analytics' && activeEvent && (
            <AnalyticsTab event={activeEvent} />
          )}
          {tab !== 'events' && tab !== 'create' && !activeEvent && (
            <EmptyState text="No events yet. Create one from the Events tab." />
          )}
        </>
      )}
    </div>
  );
}

// ── Event selector dropdown ──────────────────────────────────────────────────
function EventSelector({ events, value, onChange }: { events: EventRow[]; value: string | null; onChange: (id: string) => void }) {
  return (
    <div style={{ marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
      <SectionLabel style={{ marginBottom: 0 }}>Event</SectionLabel>
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: '320px' }}>
        {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}, {fmtDate(ev.start_date)}</option>)}
      </select>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 1, EVENTS
// ════════════════════════════════════════════════════════════════════════════
function EventsTab({ events, regCounts, onCreate, onManage, onEdit }: {
  events: EventRow[]; regCounts: Record<string, number>;
  onCreate: () => void; onManage: (id: string) => void; onEdit: (id: string) => void;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
        <button style={btnGold} onClick={onCreate}>+ Create Event</button>
      </div>
      {events.length === 0 ? <EmptyState text="No events yet. Create your first conference." /> : (
        <div style={{ ...card, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {['Event', 'Dates', 'Location', 'Status', 'Registered', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map(ev => (
                <tr key={ev.id}>
                  <td style={{ padding: '12px 14px', borderBottom: `1px solid ${C.borderLt}` }}>
                    <div style={{ fontFamily: 'Georgia, serif', fontSize: '14px', color: C.navy }}>{ev.name}</div>
                    {ev.venue && <div style={{ fontSize: '11px', color: C.muted }}>{ev.venue}</div>}
                  </td>
                  <td style={{ padding: '12px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{fmtDateRange(ev.start_date, ev.end_date)}</td>
                  <td style={{ padding: '12px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{ev.location || '-'}</td>
                  <td style={{ padding: '12px 14px', borderBottom: `1px solid ${C.borderLt}` }}><StatusBadge status={ev.status} /></td>
                  <td style={{ padding: '12px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '13px', color: C.text, fontWeight: 600 }}>{regCounts[ev.id] ?? 0}</td>
                  <td style={{ padding: '12px 14px', borderBottom: `1px solid ${C.borderLt}`, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button style={{ ...btnGhost, marginRight: '6px' }} onClick={() => onEdit(ev.id)}>Edit</button>
                    <button style={btnPrimary} onClick={() => onManage(ev.id)}>Manage</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 2, CREATE / EDIT
// ════════════════════════════════════════════════════════════════════════════
interface DraftSession extends Partial<PlenarySession> { localId: string; speakers: (Partial<Speaker> & { localId: string })[]; }

function CreateEditTab({ userId, event, onSaved, onClose }: {
  userId: string | null; event: EventRow | null; onSaved: (id: string) => Promise<void>; onClose: () => void;
}) {
  const [form, setForm] = useState<Partial<EventRow>>(event || {
    name: '', description: '', location: '', venue: '',
    start_date: '', end_date: '', status: 'draft',
    max_attendees_per_firm: 2, concurrent_presenting_slots: 3,
    investor_preference_limit: 5, investor_preference_buffer: 2,
    session_duration_minutes: 30, plenary_duration_minutes: 60,
    npm_meeting_duration_minutes: 30, schedule_change_deadline_days: 1,
    agenda_url: '', pulse_survey_id: null,
  });
  const [sessions, setSessions] = useState<DraftSession[]>([]);
  const [surveys, setSurveys] = useState<{ id: string; title: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const dragIdx = useRef<number | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('pulse_surveys').select('id, title').order('created_at', { ascending: false });
      setSurveys(data || []);
      if (event) {
        const { data: ps } = await supabase.from('event_plenary_sessions').select('*').eq('event_id', event.id).order('order_index');
        const { data: sp } = await supabase.from('event_speakers').select('*').eq('event_id', event.id).order('order_index');
        const speakersBySession: Record<string, (Partial<Speaker> & { localId: string })[]> = {};
        (sp || []).forEach((s: Speaker, i: number) => { (speakersBySession[s.plenary_session_id || ''] ||= []).push({ ...s, localId: `sp-${s.id || i}` }); });
        setSessions((ps || []).map((p: PlenarySession, i: number) => ({ ...p, localId: `s-${p.id || i}`, speakers: speakersBySession[p.id] || [] })));
      }
    })();
  }, [event]);

  const set = (patch: Partial<EventRow>) => setForm(f => ({ ...f, ...patch }));

  const addSession = () => setSessions(s => [...s, { localId: `s-${Date.now()}`, title: '', session_type: 'keynote', is_plenary: true, start_time: '', end_time: '', location: '', description: '', speakers: [] }]);
  const updateSession = (idx: number, patch: Partial<DraftSession>) => setSessions(s => s.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const removeSession = (idx: number) => setSessions(s => s.filter((_, i) => i !== idx));
  const moveSession = (from: number, to: number) => {
    if (to < 0 || to >= sessions.length) return;
    setSessions(s => { const n = [...s]; const [it] = n.splice(from, 1); n.splice(to, 0, it); return n; });
  };
  const addSpeaker = (si: number) => updateSession(si, { speakers: [...sessions[si].speakers, { localId: `sp-${Date.now()}`, name: '', title: '', company: '', bio: '' }] });
  const updateSpeaker = (si: number, spi: number, patch: Partial<Speaker>) =>
    updateSession(si, { speakers: sessions[si].speakers.map((x, i) => i === spi ? { ...x, ...patch } : x) });
  const removeSpeaker = (si: number, spi: number) => updateSession(si, { speakers: sessions[si].speakers.filter((_, i) => i !== spi) });

  async function uploadAgenda(file: File) {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'pdf';
      const path = `agendas/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from(AGENDA_BUCKET).upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from(AGENDA_BUCKET).getPublicUrl(path);
      set({ agenda_url: data.publicUrl });
      setBanner({ type: 'ok', text: 'Agenda uploaded.' });
    } catch (e: any) {
      setBanner({ type: 'err', text: `Agenda upload failed (${e.message || 'bucket may not exist'}). You can paste a URL instead.` });
    } finally { setUploading(false); }
  }

  async function save(publish: boolean) {
    if (!form.name || !form.start_date || !form.end_date) { setBanner({ type: 'err', text: 'Name, start date and end date are required.' }); return; }
    setSaving(true);
    try {
      const payload = { ...form, status: publish ? (form.status === 'draft' ? 'open' : form.status) : (form.status || 'draft'), created_by: event?.created_by || userId, updated_at: new Date().toISOString() };
      delete (payload as any).id; delete (payload as any).created_at;
      let eventId = event?.id;
      if (event) {
        await supabase.from('events').update(payload).eq('id', event.id);
      } else {
        const { data, error } = await supabase.from('events').insert(payload).select('id').single();
        if (error) throw error;
        eventId = data.id;
      }
      if (!eventId) throw new Error('No event id');

      // Replace plenary sessions + speakers (simple strategy).
      await supabase.from('event_plenary_sessions').delete().eq('event_id', eventId);
      for (let i = 0; i < sessions.length; i++) {
        const s = sessions[i];
        if (!s.title || !s.start_time || !s.end_time) continue;
        const { data: sess, error: se } = await supabase.from('event_plenary_sessions').insert({
          event_id: eventId, title: s.title, session_type: s.session_type || 'keynote',
          is_plenary: s.is_plenary ?? true, start_time: s.start_time, end_time: s.end_time,
          location: s.location || null, description: s.description || null, order_index: i,
        }).select('id').single();
        if (se || !sess) continue;
        const speakerRows = s.speakers.filter(sp => sp.name).map((sp, j) => ({
          event_id: eventId, plenary_session_id: sess.id, name: sp.name!, title: sp.title || null,
          company: sp.company || null, bio: sp.bio || null, photo_url: sp.photo_url || null, order_index: j,
        }));
        if (speakerRows.length) await supabase.from('event_speakers').insert(speakerRows);
      }
      setBanner({ type: 'ok', text: publish ? 'Event published.' : 'Event saved as draft.' });
      await onSaved(eventId);
    } catch (e: any) {
      setBanner({ type: 'err', text: `Save failed: ${e.message || e}` });
    } finally { setSaving(false); }
  }

  const numField = (label: string, key: keyof EventRow, hint?: string) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type="number" value={(form[key] as number) ?? 0} onChange={e => set({ [key]: parseInt(e.target.value, 10) || 0 } as any)} style={inputStyle} />
      {hint && <div style={{ fontSize: '10.5px', color: C.mutedLt, marginTop: '3px' }}>{hint}</div>}
    </div>
  );

  return (
    <div style={{ maxWidth: '860px' }}>
      {banner && <Banner type={banner.type} text={banner.text} />}
      <div style={{ ...card, padding: '20px', marginBottom: '16px' }}>
        <SectionLabel style={{ marginBottom: '14px' }}>{event ? 'Edit Event' : 'New Event'}</SectionLabel>
        <div style={{ display: 'grid', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Event Name *</label>
            <input value={form.name || ''} onChange={e => set({ name: e.target.value })} style={inputStyle} placeholder="Morgan Stanley Global Alts Conference" />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description || ''} onChange={e => set({ description: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div><label style={labelStyle}>Location</label><input value={form.location || ''} onChange={e => set({ location: e.target.value })} style={inputStyle} placeholder="Miami, FL" /></div>
            <div><label style={labelStyle}>Venue</label><input value={form.venue || ''} onChange={e => set({ venue: e.target.value })} style={inputStyle} placeholder="Fontainebleau" /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div><label style={labelStyle}>Start Date *</label><input type="date" value={form.start_date || ''} onChange={e => set({ start_date: e.target.value })} style={inputStyle} /></div>
            <div><label style={labelStyle}>End Date *</label><input type="date" value={form.end_date || ''} onChange={e => set({ end_date: e.target.value })} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
            {numField('Max Attendees / Firm', 'max_attendees_per_firm')}
            {numField('Concurrent Slots / Round', 'concurrent_presenting_slots')}
            {numField('Session Duration (min)', 'session_duration_minutes')}
            {numField('Preference Limit', 'investor_preference_limit')}
            {numField('Preference Buffer', 'investor_preference_buffer')}
            {numField('NPM Duration (min)', 'npm_meeting_duration_minutes')}
            {numField('Plenary Duration (min)', 'plenary_duration_minutes')}
            {numField('Change Deadline (days)', 'schedule_change_deadline_days', 'Days before event that changes lock')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Agenda PDF</label>
              <input type="file" accept="application/pdf" onChange={e => e.target.files?.[0] && uploadAgenda(e.target.files[0])} style={{ fontSize: '12px' }} />
              {uploading && <div style={{ fontSize: '11px', color: C.muted, marginTop: '4px' }}>Uploading…</div>}
              {form.agenda_url && <a href={form.agenda_url} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: C.teal, marginTop: '4px', display: 'block' }}>View uploaded agenda</a>}
            </div>
            <div>
              <label style={labelStyle}>Link PulseConnect Survey</label>
              <select value={form.pulse_survey_id || ''} onChange={e => set({ pulse_survey_id: e.target.value || null })} style={inputStyle}>
                <option value="">, None, </option>
                {surveys.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Plenary sessions builder */}
      <div style={{ ...card, padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <SectionLabel style={{ marginBottom: 0 }}>Plenary Sessions</SectionLabel>
          <button style={btnGhost} onClick={addSession}>+ Add Session</button>
        </div>
        {sessions.length === 0 && <div style={{ fontSize: '12px', color: C.mutedLt }}>No plenary sessions yet.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {sessions.map((s, i) => (
            <div key={s.localId} draggable
              onDragStart={() => { dragIdx.current = i; }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); if (dragIdx.current !== null) moveSession(dragIdx.current, i); dragIdx.current = null; }}
              style={{ border: `1px solid ${C.border}`, borderRadius: '4px', padding: '14px', background: '#FCFCFD' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ color: C.mutedLt, cursor: 'grab' }}>⋮⋮</span>
                <input value={s.title || ''} onChange={e => updateSession(i, { title: e.target.value })} placeholder="Session title" style={{ ...inputStyle, flex: 1 }} />
                <select value={s.session_type} onChange={e => updateSession(i, { session_type: e.target.value as SessionType })} style={{ ...inputStyle, width: 'auto' }}>
                  {SESSION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <button style={btnDanger} onClick={() => removeSession(i)}>Remove</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div><label style={labelStyle}>Start</label><input type="datetime-local" value={toLocalInput(s.start_time || null)} onChange={e => updateSession(i, { start_time: e.target.value ? new Date(e.target.value).toISOString() : '' })} style={inputStyle} /></div>
                <div><label style={labelStyle}>End</label><input type="datetime-local" value={toLocalInput(s.end_time || null)} onChange={e => updateSession(i, { end_time: e.target.value ? new Date(e.target.value).toISOString() : '' })} style={inputStyle} /></div>
                <div><label style={labelStyle}>Location</label><input value={s.location || ''} onChange={e => updateSession(i, { location: e.target.value })} style={inputStyle} /></div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: C.textSec, marginBottom: '10px' }}>
                <input type="checkbox" checked={s.is_plenary ?? true} onChange={e => updateSession(i, { is_plenary: e.target.checked })} /> Plenary (visible to all)
              </label>
              {/* Speakers */}
              <div style={{ borderTop: `1px solid ${C.borderLt}`, paddingTop: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <SectionLabel style={{ marginBottom: 0 }}>Speakers</SectionLabel>
                  <button style={btnGhost} onClick={() => addSpeaker(i)}>+ Speaker</button>
                </div>
                {s.speakers.map((sp, spi) => (
                  <div key={sp.localId} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', marginBottom: '6px' }}>
                    <input value={sp.name || ''} onChange={e => updateSpeaker(i, spi, { name: e.target.value })} placeholder="Name" style={inputStyle} />
                    <input value={sp.title || ''} onChange={e => updateSpeaker(i, spi, { title: e.target.value })} placeholder="Title" style={inputStyle} />
                    <input value={sp.company || ''} onChange={e => updateSpeaker(i, spi, { company: e.target.value })} placeholder="Company" style={inputStyle} />
                    <button style={btnDanger} onClick={() => removeSpeaker(i, spi)}>×</button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button style={btnGhost} onClick={onClose}>Cancel</button>
        <button style={btnGhost} disabled={saving} onClick={() => save(false)}>Save as Draft</button>
        <button style={btnGold} disabled={saving} onClick={() => save(true)}>{saving ? 'Saving…' : 'Publish'}</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 3, REGISTRATIONS & SELECTION
// ════════════════════════════════════════════════════════════════════════════
// A manager firm on the platform (firm owned by a profile whose role is 'manager').
interface PlatformFirm {
  id: string;
  name: string;
  headquarters: string | null;
  aum_min: number | null;
  aum_max: number | null;
  aum_exact: number | null;
  owner_id: string;
  owner?: { id: string; role: string; full_name: string } | null;
}

function RegistrationsTab({ event, userId, onReload }: { event: EventRow; userId: string | null; onReload: () => void }) {
  const [sub, setSub] = useState<'registrations' | 'selection'>('registrations');
  const [regs, setRegs] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  // Add-manager-from-platform search
  const [mgrSearch, setMgrSearch] = useState('');
  const [mgrResults, setMgrResults] = useState<PlatformFirm[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('event_registrations')
      .select('*, profile:profiles(id,full_name,email,role,avatar_url), firm:firms(id,name,type,headquarters,aum_min,aum_max,aum_exact)')
      .eq('event_id', event.id).order('registered_at');
    setRegs((data || []) as Registration[]);
    setLoading(false);
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  // Live search of ALL manager firms on the platform (debounced 300ms).
  useEffect(() => {
    const q = mgrSearch.trim();
    if (!q) { setMgrResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      const { data } = await supabase.from('firms')
        .select('id, name, headquarters, aum_min, aum_max, aum_exact, owner_id, owner:profiles!firms_owner_id_fkey(id, role, full_name)')
        .not('owner_id', 'is', null)
        .ilike('name', `%${q}%`)
        .order('name')
        .limit(20);
      // Keep only firms whose owner is a manager.
      const mgr = ((data || []) as any[]).filter(f => f.owner?.role === 'manager') as PlatformFirm[];
      setMgrResults(mgr);
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [mgrSearch]);

  // Add a platform manager firm to this event as a selected presenting/NPM manager.
  async function addFromPlatform(firm: PlatformFirm, role: 'presenting_manager' | 'npm_manager') {
    setAddingId(firm.id);
    const { error } = await supabase.from('event_registrations').insert({
      event_id: event.id,
      profile_id: firm.owner_id,
      firm_id: firm.id,
      role_at_event: role,
      status: 'selected',
      added_by_pb: true,
    });
    // If they already have a registration (unique event_id+profile_id), promote it instead.
    if (error) {
      await supabase.from('event_registrations')
        .update({ role_at_event: role, status: 'selected' })
        .eq('event_id', event.id).eq('profile_id', firm.owner_id);
    }
    await load();
    setAddingId(null);
  }

  async function removeReg(reg: Registration) {
    await supabase.from('event_registrations').delete().eq('id', reg.id);
    load();
  }

  const filtered = regs.filter(r => {
    if (roleFilter && r.role_at_event !== roleFilter) return false;
    if (statusFilter && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(r.profile?.full_name?.toLowerCase().includes(q) || r.firm?.name?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const managers = regs.filter(r => r.profile?.role === 'manager');
  const allocators = regs.filter(r => r.profile?.role === 'allocator');

  async function setRole(reg: Registration, role: 'presenting_manager' | 'npm_manager', selected: boolean) {
    await supabase.from('event_registrations').update({
      role_at_event: selected ? role : null,
      status: selected ? 'selected' : 'interested',
    }).eq('id', reg.id);
    load();
  }

  async function notifySelected() {
    setSaving(true);
    const selected = regs.filter(r => r.status === 'selected' && (r.role_at_event === 'presenting_manager' || r.role_at_event === 'npm_manager'));
    const ids = selected.map(r => r.profile_id).filter(Boolean) as string[];
    await notifyManagersSelected(event, ids);
    // move event into manager_setup if still open
    if (event.status === 'open') await supabase.from('events').update({ status: 'manager_setup' }).eq('id', event.id);
    setBanner(`Notified ${ids.length} selected managers.`);
    setSaving(false);
    onReload();
  }

  if (loading) return <LoadingScreen />;

  return (
    <div>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {(['registrations', 'selection'] as const).map(s => (
          <button key={s} onClick={() => setSub(s)} style={{ ...btnGhost, background: sub === s ? C.navy : C.white, color: sub === s ? C.white : C.textSec, border: sub === s ? 'none' : `1px solid ${C.border}`, textTransform: 'capitalize' }}>{s}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <Pill text={`${managers.filter(m => !(m as any).added_by_pb).length} registered interest`} color={C.navy} bg="#EFF6FF" bd="#BFDBFE" />
        <Pill text={`${managers.filter(m => (m as any).added_by_pb).length} added by Altveno`} color={C.teal} bg="#F0FDFA" bd="#99F6E4" />
        <Pill text={`${regs.filter(r => r.role_at_event === 'presenting_manager').length} presenting selected`} color={C.amber} bg="#FEFCE8" bd="#FDE68A" />
        <Pill text={`${regs.filter(r => r.role_at_event === 'npm_manager').length} NPM selected`} color={C.success} bg="#F0FDF4" bd="#BBF7D0" />
        <Pill text={`${allocators.length} allocators registered`} color={C.textSec} bg={C.surface} bd={C.border} />
      </div>

      {sub === 'registrations' ? (
        <div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <input placeholder="Search name or firm…" value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: '240px' }} />
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="">All roles</option>
              <option value="presenting_manager">Presenting Manager</option>
              <option value="npm_manager">NPM Manager</option>
              <option value="allocator">Allocator</option>
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="">All statuses</option>
              {['interested', 'selected', 'confirmed', 'declined'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {filtered.length === 0 ? <EmptyState text="No registrations match." /> : (
            <div style={{ ...card, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
                <thead><tr style={{ background: C.surface }}>{['Name', 'Firm', 'Type', 'Role at Event', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id}>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px', color: C.text, fontWeight: 500 }}>{r.profile?.full_name || '-'}</td>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{r.firm?.name || '-'}</td>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.muted, textTransform: 'capitalize' }}>{r.profile?.role || '-'}</td>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{r.role_at_event?.replace(/_/g, ' ') || '-'}</td>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}` }}><Pill text={r.status} color={C.textSec} bg={C.surface} bd={C.border} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div>
          {banner && <Banner type="ok" text={banner} />}
          <Banner type="info" text="Select managers as Presenting or NPM. Selected managers receive a setup notification." />

          {/* Add any manager firm from the platform */}
          <div style={{ ...card, padding: '16px', marginBottom: '14px' }}>
            <SectionLabel style={{ marginBottom: '8px' }}>Add Manager from Platform</SectionLabel>
            <div style={{ fontSize: '11.5px', color: C.muted, marginBottom: '10px' }}>
              Search every manager firm on the platform and add it straight into this event as a presenting or NPM manager, no prior interest needed.
            </div>
            <div style={{ position: 'relative', maxWidth: '480px' }}>
              <input
                placeholder="Search manager firms by name…"
                value={mgrSearch}
                onChange={e => setMgrSearch(e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
              />
              {mgrSearch.trim() !== '' && (
                <div style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, marginTop: '4px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 6px 24px rgba(0,0,0,0.10)', maxHeight: '340px', overflowY: 'auto' }}>
                  {searching ? (
                    <div style={{ padding: '12px 14px', fontSize: '12px', color: C.muted }}>Searching…</div>
                  ) : mgrResults.length === 0 ? (
                    <div style={{ padding: '12px 14px', fontSize: '12px', color: C.mutedLt }}>No manager firms match.</div>
                  ) : mgrResults.map(f => {
                    const already = regs.some(r => r.firm_id === f.id);
                    const aum = aumLabel(f);
                    return (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12.5px', color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                          <div style={{ fontSize: '11px', color: C.muted }}>{f.headquarters || '-'}{aum ? ` · ${aum}` : ''}</div>
                        </div>
                        {already ? (
                          <Pill text="Already registered" color={C.muted} bg={C.surface} bd={C.border} />
                        ) : (
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                            <button style={{ ...btnPrimary, padding: '5px 10px' }} disabled={addingId === f.id} onClick={() => addFromPlatform(f, 'presenting_manager')}>{addingId === f.id ? '…' : 'Add as Presenting'}</button>
                            <button style={{ ...btnGhost, padding: '5px 10px' }} disabled={addingId === f.id} onClick={() => addFromPlatform(f, 'npm_manager')}>Add as NPM</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {managers.length === 0 ? <EmptyState text="No managers registered yet. Add one from the platform above." /> : (
          <div style={{ ...card, overflow: 'hidden', marginBottom: '14px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
              <thead><tr style={{ background: C.surface }}>{['Firm', 'HQ', 'AUM', 'Added Via', 'Presenting', 'NPM', 'Status', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
              <tbody>
                {managers.map(r => {
                  const byPB = Boolean((r as any).added_by_pb);
                  const firm = r.firm as any;
                  return (
                  <tr key={r.id}>
                    <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px', color: C.text, fontWeight: 500 }}>{r.firm?.name || r.profile?.full_name || '-'}</td>
                    <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{firm?.headquarters || '-'}</td>
                    <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{aumLabel(firm) || '-'}</td>
                    <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}` }}>
                      {byPB
                        ? <Pill text="Added by Altveno" color={C.teal} bg="#F0FDFA" bd="#99F6E4" />
                        : <Pill text="Registered Interest" color={C.navy} bg="#EFF6FF" bd="#BFDBFE" />}
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}` }}>
                      <input type="checkbox" checked={r.role_at_event === 'presenting_manager'} onChange={e => setRole(r, 'presenting_manager', e.target.checked)} />
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}` }}>
                      <input type="checkbox" checked={r.role_at_event === 'npm_manager'} onChange={e => setRole(r, 'npm_manager', e.target.checked)} />
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}` }}><Pill text={r.status} color={C.textSec} bg={C.surface} bd={C.border} /></td>
                    <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.borderLt}`, textAlign: 'right' }}>
                      <button style={btnDanger} onClick={() => removeReg(r)}>Remove</button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          <button style={btnGold} disabled={saving} onClick={notifySelected}>{saving ? 'Notifying…' : 'Notify Selected Managers'}</button>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 4, SCHEDULING
// ════════════════════════════════════════════════════════════════════════════
const WORKFLOW_STEPS = ['Availability', 'Preferences', 'AI Slots', 'Confirmation', 'Published'];

function statusToStep(s: EventStatus): number {
  switch (s) {
    case 'manager_setup': return 0;
    case 'investor_preferences': return 1;
    case 'scheduling': return 2;
    case 'published': case 'live': return 4;
    case 'completed': return 4;
    default: return 0;
  }
}

function SchedulingTab({ event, userId, onEventChanged }: { event: EventRow; userId: string | null; onEventChanged: () => void }) {
  const [managers, setManagers] = useState<Registration[]>([]);
  const [availability, setAvailability] = useState<ManagerAvailability[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [slots, setSlots] = useState<ManagerSlot[]>([]);
  const [prefs, setPrefs] = useState<InvestorPreference[]>([]);
  const [schedule, setSchedule] = useState<InvestorSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err' | 'info'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [mgr, avail, rnd, slt, prf, sch] = await Promise.all([
      supabase.from('event_registrations').select('*, profile:profiles(id,full_name), firm:firms(id,name,type)').eq('event_id', event.id).eq('role_at_event', 'presenting_manager'),
      supabase.from('event_manager_availability').select('*').eq('event_id', event.id),
      supabase.from('event_rounds').select('*').eq('event_id', event.id).order('round_number'),
      supabase.from('event_manager_slots').select('*, round:event_rounds(*), firm:firms(id,name,type)').eq('event_id', event.id),
      supabase.from('event_investor_preferences').select('*, profile:profiles(id,full_name), manager_firm:firms!event_investor_preferences_manager_firm_id_fkey(id,name)').eq('event_id', event.id),
      supabase.from('event_investor_schedule').select('*').eq('event_id', event.id),
    ]);
    setManagers((mgr.data || []) as Registration[]);
    setAvailability((avail.data || []) as ManagerAvailability[]);
    setRounds((rnd.data || []) as Round[]);
    setSlots((slt.data || []) as ManagerSlot[]);
    setPrefs((prf.data || []) as InvestorPreference[]);
    setSchedule((sch.data || []) as InvestorSchedule[]);
    setLoading(false);
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const availByFirm = useMemo(() => {
    const m: Record<string, ManagerAvailability> = {};
    availability.forEach(a => { if (a.firm_id) m[a.firm_id] = a; });
    return m;
  }, [availability]);

  const submittedCount = managers.filter(m => m.firm_id && availByFirm[m.firm_id]).length;
  const confirmedSlots = slots.filter(s => s.status === 'confirmed').length;
  const distinctManagerFirms = Array.from(new Set(slots.map(s => s.firm_id).filter(Boolean))) as string[];

  // ── Run AI slot builder ────────────────────────────────────────────────────
  async function runSlotBuilder() {
    setRunning(true); setMsg(null);
    try {
      const managerList = managers.filter(m => m.firm_id).map(m => ({ firm_id: m.firm_id!, firm_name: m.firm?.name || 'Manager' }));
      const avail: Record<string, { rounds: number[]; multiple: boolean }> = {};
      managerList.forEach(m => {
        const a = availByFirm[m.firm_id];
        avail[m.firm_id] = { rounds: a?.available_round_numbers || [], multiple: a?.can_present_multiple_rounds || false };
      });
      const result = await runAISlotBuilder({
        managers: managerList, availability: avail,
        roundCount: Math.ceil(managerList.length / Math.max(1, event.concurrent_presenting_slots)),
        concurrentSlots: event.concurrent_presenting_slots,
      });

      // (Re)create rounds with times: start 09:00 on event start_date, sequential blocks.
      await supabase.from('event_rounds').delete().eq('event_id', event.id); // cascades to slots
      const base = new Date(event.start_date + 'T09:00:00');
      const dur = event.session_duration_minutes || 30;
      const roundRows = Array.from({ length: result.roundsNeeded }, (_, i) => {
        const start = new Date(base.getTime() + i * (dur + 10) * 60000);
        const end = new Date(start.getTime() + dur * 60000);
        return { event_id: event.id, round_number: i + 1, start_time: start.toISOString(), end_time: end.toISOString() };
      });
      const { data: newRounds } = await supabase.from('event_rounds').insert(roundRows).select('*');
      const roundIdByNum: Record<number, string> = {};
      (newRounds || []).forEach((r: Round) => { roundIdByNum[r.round_number] = r.id; });

      const slotRows = result.assignments.map(a => ({
        event_id: event.id, round_id: roundIdByNum[a.round_number], firm_id: a.firm_id,
        capacity: 10, status: 'ai_suggested', ai_score: a.ai_score,
      })).filter(s => s.round_id);
      if (slotRows.length) await supabase.from('event_manager_slots').insert(slotRows);

      await supabase.from('events').update({ status: 'scheduling' }).eq('id', event.id);
      setMsg({ type: 'ok', text: result.narrative + (result.unassigned.length ? ` Unplaced: ${result.unassigned.join(', ')}.` : '') });
      await load(); onEventChanged();
    } catch (e: any) {
      setMsg({ type: 'err', text: `Slot builder failed: ${e.message || e}` });
    } finally { setRunning(false); }
  }

  async function openPreferences() {
    setRunning(true);
    try {
      await supabase.from('events').update({ status: 'investor_preferences' }).eq('id', event.id);
      const { data: allocs } = await supabase.from('event_registrations').select('profile_id').eq('event_id', event.id).eq('role_at_event', 'allocator');
      await notifyPreferencesOpen(event, (allocs || []).map((a: any) => a.profile_id).filter(Boolean));
      setMsg({ type: 'ok', text: 'Investor preferences opened and allocators notified.' });
      onEventChanged();
    } finally { setRunning(false); }
  }

  // ── Run AI schedule optimisation ───────────────────────────────────────────
  async function runOptimisation() {
    setRunning(true); setMsg(null);
    try {
      // Distinct investors from preferences.
      const investorMap: Record<string, { profile_id: string; firm_id: string | null; name: string }> = {};
      prefs.forEach(p => { if (p.profile_id) investorMap[p.profile_id] = { profile_id: p.profile_id, firm_id: p.firm_id, name: p.profile?.full_name || 'Investor' }; });
      const preferences: Record<string, { manager_firm_id: string; rank: number }[]> = {};
      prefs.forEach(p => { if (p.profile_id && p.manager_firm_id) (preferences[p.profile_id] ||= []).push({ manager_firm_id: p.manager_firm_id, rank: p.preference_rank }); });

      const slotInputs = slots.filter(s => s.round && s.firm_id).map(s => ({
        slot_id: s.id, firm_id: s.firm_id!, round_number: s.round!.round_number, capacity: s.capacity,
      }));

      const [mgrEx, pbEx] = await Promise.all([
        supabase.from('event_manager_exclusions').select('manager_firm_id, excluded_profile_id').eq('event_id', event.id),
        supabase.from('event_pb_exclusions').select('manager_slot_id, excluded_profile_id').eq('event_id', event.id),
      ]);
      const managerExclusions = new Set((mgrEx.data || []).map((e: any) => `${e.manager_firm_id}:${e.excluded_profile_id}`));
      const pbExclusions = new Set((pbEx.data || []).map((e: any) => `${e.manager_slot_id}:${e.excluded_profile_id}`));

      const result = await runAIScheduleOptimisation({
        investors: Object.values(investorMap), preferences, slots: slotInputs, managerExclusions, pbExclusions,
      });

      await supabase.from('event_investor_schedule').delete().eq('event_id', event.id);
      const rows = result.assignments.map(a => ({
        event_id: event.id, profile_id: a.profile_id, firm_id: investorMap[a.profile_id]?.firm_id || null,
        manager_slot_id: a.slot_id, assignment_type: a.assignment_type, preference_rank: a.preference_rank, status: 'confirmed',
      }));
      if (rows.length) await supabase.from('event_investor_schedule').insert(rows);
      setMsg({ type: 'ok', text: `${result.narrative} (${result.satisfactionRate}% preference-match)` });
      await load();
    } catch (e: any) {
      setMsg({ type: 'err', text: `Optimisation failed: ${e.message || e}` });
    } finally { setRunning(false); }
  }

  async function publishSchedules() {
    setRunning(true);
    try {
      await supabase.from('events').update({ status: 'published' }).eq('id', event.id);
      const { data: allReg } = await supabase.from('event_registrations').select('profile_id').eq('event_id', event.id);
      await notifySchedulePublished(event, (allReg || []).map((a: any) => a.profile_id).filter(Boolean));
      setMsg({ type: 'ok', text: 'Schedules published and all attendees notified.' });
      onEventChanged();
    } finally { setRunning(false); }
  }

  // Preference heatmap: managers by total preferences received.
  const prefHeat = useMemo(() => {
    const counts: Record<string, { name: string; count: number }> = {};
    prefs.forEach(p => {
      if (!p.manager_firm_id) return;
      counts[p.manager_firm_id] ||= { name: p.manager_firm?.name || 'Manager', count: 0 };
      counts[p.manager_firm_id].count += 1;
    });
    return Object.values(counts).sort((a, b) => b.count - a.count);
  }, [prefs]);

  const distinctInvestors = useMemo(() => new Set(prefs.map(p => p.profile_id).filter(Boolean)).size, [prefs]);

  if (loading) return <LoadingScreen />;

  return (
    <div>
      <ProgressBar steps={WORKFLOW_STEPS} current={statusToStep(event.status)} />
      {msg && <Banner type={msg.type} text={msg.text} />}

      {/* Manager availability */}
      <Section title="Manager Availability" sub={`${submittedCount} of ${managers.length} presenting managers submitted`}>
        <div style={{ ...card, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
            <thead><tr style={{ background: C.surface }}>{['Manager Firm', 'Submitted', 'Available Rounds', 'Multiple'].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
            <tbody>
              {managers.map(m => {
                const a = m.firm_id ? availByFirm[m.firm_id] : undefined;
                return (
                  <tr key={m.id}>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px', color: C.text }}>{m.firm?.name || '-'}</td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}` }}>{a ? <Pill text="Yes" color={C.success} bg="#F0FDF4" bd="#BBF7D0" /> : <Pill text="Pending" color={C.muted} bg={C.surface} bd={C.border} />}</td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{a?.available_round_numbers?.join(', ') || '-'}</td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{a?.can_present_multiple_rounds ? 'Yes' : 'No'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* AI slot builder */}
      <Section title="AI Slot Builder" sub={slots.length ? `${slots.length} slots across ${rounds.length} rounds` : 'Assign managers to presenting rounds'}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
          <button style={btnGold} disabled={running || managers.length === 0} onClick={runSlotBuilder}>{running ? 'Running…' : 'Run AI Scheduling'}</button>
          {slots.length > 0 && event.status !== 'investor_preferences' && (
            <button style={btnPrimary} disabled={running} onClick={openPreferences}>Open Investor Preferences</button>
          )}
        </div>
        {rounds.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(rounds.length, 4)}, 1fr)`, gap: '12px' }}>
            {rounds.map(r => (
              <div key={r.id} style={{ ...card, padding: '12px' }}>
                <SectionLabel>Round {r.round_number}</SectionLabel>
                <div style={{ fontSize: '11px', color: C.muted, marginBottom: '8px' }}>{fmtTimeRange(r.start_time, r.end_time)}</div>
                {slots.filter(s => s.round_id === r.id).map(s => (
                  <div key={s.id} style={{ fontSize: '12px', color: C.text, padding: '5px 8px', background: C.surface, borderRadius: '3px', marginBottom: '5px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{s.firm?.name}</span>
                    {s.ai_score != null && <span style={{ color: C.mutedLt, fontSize: '10px' }}>{Math.round(s.ai_score * 100)}%</span>}
                  </div>
                ))}
                {slots.filter(s => s.round_id === r.id).length === 0 && <div style={{ fontSize: '11px', color: C.mutedLt }}>Empty</div>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Investor preferences */}
      <Section title="Investor Preferences" sub={`${distinctInvestors} investors submitted preferences`}>
        {prefHeat.length === 0 ? <div style={{ fontSize: '12px', color: C.mutedLt }}>No preferences submitted yet.</div> : (
          <>
            <div style={{ ...card, padding: '16px', marginBottom: '12px' }}>
              <SectionLabel style={{ marginBottom: '10px' }}>Manager Popularity (preferences received)</SectionLabel>
              {prefHeat.map(h => {
                const max = prefHeat[0].count || 1;
                return (
                  <div key={h.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <span style={{ width: '180px', fontSize: '12px', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</span>
                    <div style={{ flex: 1, height: '14px', background: C.surface, borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ width: `${(h.count / max) * 100}%`, height: '100%', background: C.navy }} />
                    </div>
                    <span style={{ fontSize: '11.5px', color: C.muted, fontWeight: 600, width: '32px', textAlign: 'right' }}>{h.count}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={btnGold} disabled={running || slots.length === 0} onClick={runOptimisation}>{running ? 'Optimising…' : 'Run AI Schedule Optimisation'}</button>
              {schedule.length > 0 && <Pill text={`${schedule.length} assignments`} color={C.success} bg="#F0FDF4" bd="#BBF7D0" />}
            </div>
          </>
        )}
      </Section>

      {/* Manager exclusion review */}
      <Section title="Manager Slot Confirmation" sub={`${confirmedSlots} of ${distinctManagerFirms.length || slots.length} manager slots confirmed`}>
        <div style={{ fontSize: '12px', color: C.textSec, marginBottom: '10px' }}>
          Managers review their investor lists and confirm slots from their setup page. Once all are confirmed, publish schedules.
        </div>
        <button style={btnPrimary} disabled={running || schedule.length === 0} onClick={publishSchedules}>Publish Schedules</button>
      </Section>

      {/* Room assignment */}
      <RoomAssignmentSection event={event} slots={slots} schedule={schedule} onReload={load} />
    </div>
  );
}

function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ marginBottom: '10px' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy }}>{title}</div>
        {sub && <div style={{ fontSize: '12px', color: C.muted }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function RoomAssignmentSection({ event, slots, schedule, onReload }: { event: EventRow; slots: ManagerSlot[]; schedule: InvestorSchedule[]; onReload: () => void }) {
  const [roomsInput, setRoomsInput] = useState('Room A, Room B, Room C, Room D');
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState<Record<string, string>>({});

  useEffect(() => {
    const m: Record<string, string> = {};
    slots.forEach(s => { m[s.id] = s.room || ''; });
    setLocal(m);
  }, [slots]);

  const investorCount = (slotId: string) => schedule.filter(s => s.manager_slot_id === slotId && s.status === 'confirmed').length;

  async function aiSuggest() {
    const rooms = roomsInput.split(',').map(r => r.trim()).filter(Boolean);
    const suggestion = suggestRooms({
      rooms,
      slots: slots.filter(s => s.round && s.firm_id).map(s => ({
        slot_id: s.id, firm_id: s.firm_id!, firm_name: s.firm?.name || '', round_number: s.round!.round_number, investorCount: investorCount(s.id),
      })),
    });
    setLocal(l => ({ ...l, ...suggestion }));
  }

  async function saveRooms() {
    setSaving(true);
    await Promise.all(Object.entries(local).map(([id, room]) => supabase.from('event_manager_slots').update({ room: room || null }).eq('id', id)));
    setSaving(false);
    onReload();
  }

  return (
    <Section title="Room Assignment" sub="Assign a room to each presenting slot">
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={roomsInput} onChange={e => setRoomsInput(e.target.value)} placeholder="Comma-separated room names" style={{ ...inputStyle, width: '320px' }} />
        <button style={btnGhost} onClick={aiSuggest}>AI Suggest Rooms</button>
        <button style={btnGold} disabled={saving} onClick={saveRooms}>{saving ? 'Saving…' : 'Save Rooms'}</button>
      </div>
      {slots.length === 0 ? <div style={{ fontSize: '12px', color: C.mutedLt }}>Run the slot builder first.</div> : (
        <div style={{ ...card, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
            <thead><tr style={{ background: C.surface }}>{['Round', 'Manager', 'Capacity', 'Registered', 'Room'].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
            <tbody>
              {slots.slice().sort((a, b) => (a.round?.round_number || 0) - (b.round?.round_number || 0)).map(s => (
                <tr key={s.id}>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{s.round ? `Round ${s.round.round_number}` : '-'}</td>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px', color: C.text }}>{s.firm?.name}</td>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{s.capacity}</td>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{investorCount(s.id)}</td>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}` }}>
                    <input value={local[s.id] || ''} onChange={e => setLocal(l => ({ ...l, [s.id]: e.target.value }))} style={{ ...inputStyle, width: '140px', padding: '5px 8px' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 5, LIVE EVENT
// ════════════════════════════════════════════════════════════════════════════
function LiveTab({ event, userId }: { event: EventRow; userId: string | null }) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState<TargetAudience>('all');
  const [sending, setSending] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [slots, setSlots] = useState<ManagerSlot[]>([]);
  const [schedule, setSchedule] = useState<InvestorSchedule[]>([]);
  const [npm, setNpm] = useState<NPMMeeting[]>([]);
  const [sent, setSent] = useState<EventNotification[]>([]);

  const load = useCallback(async () => {
    const [slt, sch, meetings, notes] = await Promise.all([
      supabase.from('event_manager_slots').select('*, round:event_rounds(*), firm:firms(id,name,type)').eq('event_id', event.id),
      supabase.from('event_investor_schedule').select('*').eq('event_id', event.id).eq('status', 'confirmed'),
      supabase.from('event_npm_meetings').select('*').eq('event_id', event.id),
      supabase.from('event_notifications').select('*').eq('event_id', event.id).order('sent_at', { ascending: false }),
    ]);
    setSlots((slt.data || []) as ManagerSlot[]);
    setSchedule((sch.data || []) as InvestorSchedule[]);
    setNpm((meetings.data || []) as NPMMeeting[]);
    setSent((notes.data || []) as EventNotification[]);
  }, [event.id]);

  useEffect(() => {
    load();
    const ch = supabase.channel(`live-${event.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_npm_meetings', filter: `event_id=eq.${event.id}` }, load)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_investor_schedule', filter: `event_id=eq.${event.id}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [event.id, load]);

  async function send() {
    if (!title || !message) { setBanner('Title and message required.'); return; }
    setSending(true);
    // Resolve recipients for the audience.
    let q = supabase.from('event_registrations').select('profile_id, role_at_event, profile:profiles(role)').eq('event_id', event.id);
    const { data } = await q;
    const recipients = (data || []).filter((r: any) => {
      if (audience === 'all') return true;
      if (audience === 'managers') return r.profile?.role === 'manager';
      if (audience === 'allocators') return r.profile?.role === 'allocator';
      if (audience === 'presenting_managers') return r.role_at_event === 'presenting_manager';
      if (audience === 'npm_managers') return r.role_at_event === 'npm_manager';
      return true;
    }).map((r: any) => r.profile_id).filter(Boolean);
    await sendEventNotification(event, userId, title, message, audience, recipients);
    setBanner(`Sent to ${recipients.length} attendees.`);
    setTitle(''); setMessage('');
    setSending(false);
    load();
  }

  const fillCount = (slotId: string) => schedule.filter(s => s.manager_slot_id === slotId).length;

  return (
    <div>
      {event.status === 'live' ? <Banner type="ok" text="Event is live. Updates stream in real time." /> :
        <Banner type="info" text={<>Event status is <strong>{event.status}</strong>. You can still compose notifications; set status to “live” from Create/Edit when the event begins.</>} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        {/* Composer */}
        <div style={{ ...card, padding: '18px' }}>
          <SectionLabel style={{ marginBottom: '12px' }}>Push Notification</SectionLabel>
          {banner && <Banner type="ok" text={banner} />}
          <div style={{ marginBottom: '10px' }}><label style={labelStyle}>Title</label><input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} /></div>
          <div style={{ marginBottom: '10px' }}><label style={labelStyle}>Message</label><textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} /></div>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Target Audience</label>
            <select value={audience} onChange={e => setAudience(e.target.value as TargetAudience)} style={inputStyle}>
              {TARGET_AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <button style={btnGold} disabled={sending} onClick={send}>{sending ? 'Sending…' : 'Send Notification'}</button>
        </div>

        {/* Sent log */}
        <div style={{ ...card, padding: '18px' }}>
          <SectionLabel style={{ marginBottom: '12px' }}>Recent Broadcasts</SectionLabel>
          {sent.length === 0 ? <div style={{ fontSize: '12px', color: C.mutedLt }}>None yet.</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '260px', overflowY: 'auto' }}>
              {sent.map(n => (
                <div key={n.id} style={{ borderLeft: `2px solid ${C.gold}`, paddingLeft: '10px' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 600, color: C.text }}>{n.title}</div>
                  <div style={{ fontSize: '11.5px', color: C.muted }}>{n.message}</div>
                  <div style={{ fontSize: '10px', color: C.mutedLt, marginTop: '2px' }}>{fmtTime(n.sent_at)} · {n.target_audience}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Session overview */}
      <Section title="Session Overview" sub="Attendee count vs capacity">
        {slots.length === 0 ? <EmptyState text="No presenting slots." /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            {slots.map(s => {
              const filled = fillCount(s.id); const pct = s.capacity ? (filled / s.capacity) * 100 : 0;
              return (
                <div key={s.id} style={{ ...card, padding: '14px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.text, marginBottom: '2px' }}>{s.firm?.name}</div>
                  <div style={{ fontSize: '11px', color: C.muted, marginBottom: '8px' }}>{s.round ? fmtTimeRange(s.round.start_time, s.round.end_time) : '-'} · {s.room || 'No room'}</div>
                  <div style={{ height: '10px', background: C.surface, borderRadius: '3px', overflow: 'hidden', marginBottom: '4px' }}>
                    <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: pct > 90 ? C.danger : C.navy }} />
                  </div>
                  <div style={{ fontSize: '11px', color: C.textSec, fontWeight: 600 }}>{filled} / {s.capacity}</div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* NPM tracker */}
      <Section title="NPM Meeting Tracker" sub={`${npm.length} requests · ${npm.filter(m => m.status === 'accepted').length} confirmed`}>
        {npm.length === 0 ? <div style={{ fontSize: '12px', color: C.mutedLt }}>No NPM meetings yet.</div> : (
          <div style={{ ...card, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
              <thead><tr style={{ background: C.surface }}>{['Requested', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
              <tbody>
                {npm.map(m => (
                  <tr key={m.id}>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec }}>{fmtDate(m.requested_at)}</td>
                    <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}` }}><Pill text={m.status} color={m.status === 'accepted' ? C.success : m.status === 'declined' ? C.danger : C.muted} bg={C.surface} bd={C.border} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 6, ANALYTICS
// ════════════════════════════════════════════════════════════════════════════
function AnalyticsTab({ event }: { event: EventRow }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{
    registrations: Registration[]; slots: ManagerSlot[]; schedule: InvestorSchedule[];
    prefs: InvestorPreference[]; npm: NPMMeeting[];
  } | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [reg, slt, sch, prf, meetings] = await Promise.all([
        supabase.from('event_registrations').select('*, profile:profiles(id,full_name,role)').eq('event_id', event.id),
        supabase.from('event_manager_slots').select('*, firm:firms(id,name)').eq('event_id', event.id),
        supabase.from('event_investor_schedule').select('*').eq('event_id', event.id),
        supabase.from('event_investor_preferences').select('*, manager_firm:firms!event_investor_preferences_manager_firm_id_fkey(id,name)').eq('event_id', event.id),
        supabase.from('event_npm_meetings').select('*').eq('event_id', event.id),
      ]);
      setData({
        registrations: (reg.data || []) as Registration[],
        slots: (slt.data || []) as ManagerSlot[],
        schedule: (sch.data || []) as InvestorSchedule[],
        prefs: (prf.data || []) as InvestorPreference[],
        npm: (meetings.data || []) as NPMMeeting[],
      });
      setLoading(false);
    })();
  }, [event.id]);

  const metrics = useMemo(() => {
    if (!data) return null;
    const managers = data.registrations.filter(r => r.profile?.role === 'manager').length;
    const allocators = data.registrations.filter(r => r.profile?.role === 'allocator').length;
    const totalCapacity = data.slots.reduce((s, x) => s + x.capacity, 0);
    const filled = data.schedule.filter(s => s.status === 'confirmed').length;
    const matched = data.schedule.filter(s => s.preference_rank != null).length;
    const satisfaction = data.schedule.length ? Math.round((matched / data.schedule.length) * 100) : 0;
    const npmReq = data.npm.length;
    const npmConf = data.npm.filter(m => m.status === 'accepted').length;
    const swaps = data.schedule.filter(s => s.assignment_type === 'investor_swap').length;
    return { managers, allocators, totalCapacity, filled, satisfaction, npmReq, npmConf, swaps,
      npmConv: npmReq ? Math.round((npmConf / npmReq) * 100) : 0,
      changeRate: data.schedule.length ? Math.round((swaps / data.schedule.length) * 100) : 0 };
  }, [data]);

  const managerPopularity = useMemo(() => {
    if (!data) return [];
    const counts: Record<string, { name: string; prefs: number }> = {};
    data.prefs.forEach(p => {
      if (!p.manager_firm_id) return;
      counts[p.manager_firm_id] ||= { name: p.manager_firm?.name || 'Manager', prefs: 0 };
      counts[p.manager_firm_id].prefs += 1;
    });
    return Object.values(counts).sort((a, b) => b.prefs - a.prefs).slice(0, 12);
  }, [data]);

  const fillRates = useMemo(() => {
    if (!data) return [];
    return data.slots.map(s => ({
      name: s.firm?.name || 'Slot',
      pct: s.capacity ? Math.round((data.schedule.filter(x => x.manager_slot_id === s.id).length / s.capacity) * 100) : 0,
    })).sort((a, b) => b.pct - a.pct).slice(0, 12);
  }, [data]);

  const satisfactionBreakdown = useMemo(() => {
    if (!data) return [];
    const byRank: Record<string, number> = {};
    data.schedule.forEach(s => {
      const k = s.preference_rank == null ? 'Alt' : `Choice ${s.preference_rank}`;
      byRank[k] = (byRank[k] || 0) + 1;
    });
    return Object.entries(byRank).map(([name, count]) => ({ name, count }))
      .sort((a, b) => (a.name === 'Alt' ? 1 : b.name === 'Alt' ? -1 : a.name.localeCompare(b.name)));
  }, [data]);

  function exportCSV() {
    if (!data || !metrics) return;
    const rows: (string | number)[][] = [
      ['Metric', 'Value'],
      ['Managers registered', metrics.managers],
      ['Allocators registered', metrics.allocators],
      ['Presenting slots filled', metrics.filled],
      ['Total capacity', metrics.totalCapacity],
      ['Preference satisfaction %', metrics.satisfaction],
      ['NPM requested', metrics.npmReq],
      ['NPM confirmed', metrics.npmConf],
      ['NPM conversion %', metrics.npmConv],
      ['Schedule change rate %', metrics.changeRate],
      [],
      ['Manager', 'Preferences received'],
      ...managerPopularity.map(m => [m.name, m.prefs]),
    ];
    downloadCSV(`${event.name}-analytics.csv`, toCSV(rows));
  }

  if (loading || !metrics) return <LoadingScreen />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
        <button style={btnGhost} onClick={exportCSV}>Export CSV</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '22px' }}>
        <StatCard label="Registrations" value={metrics.managers + metrics.allocators} sub={`${metrics.managers} mgr · ${metrics.allocators} alloc`} />
        <StatCard label="Slots Filled" value={`${metrics.filled}`} sub={`of ${metrics.totalCapacity} capacity`} />
        <StatCard label="Pref. Satisfaction" value={`${metrics.satisfaction}%`} accent={C.success} />
        <StatCard label="NPM Conversion" value={`${metrics.npmConv}%`} sub={`${metrics.npmConf}/${metrics.npmReq}`} accent={C.teal} />
        <StatCard label="Change Rate" value={`${metrics.changeRate}%`} sub={`${metrics.swaps} swaps`} accent={C.amber} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div style={{ ...card, padding: '18px' }}>
          <SectionLabel style={{ marginBottom: '12px' }}>Manager Popularity</SectionLabel>
          {managerPopularity.length === 0 ? <div style={{ fontSize: '12px', color: C.mutedLt }}>No preference data.</div> : (
            <ResponsiveContainer width="100%" height={Math.max(200, managerPopularity.length * 26)}>
              <BarChart data={managerPopularity} layout="vertical" margin={{ left: 20, right: 20, top: 4, bottom: 4 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10, fill: C.textSec }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: C.navy, border: 'none', borderRadius: '4px', fontSize: '12px', color: C.white }} cursor={{ fill: 'rgba(10,22,40,0.04)' }} />
                <Bar dataKey="prefs" fill={C.navy} radius={[0, 3, 3, 0]} maxBarSize={18}><LabelList dataKey="prefs" position="right" style={{ fontSize: 10, fill: C.muted }} /></Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div style={{ ...card, padding: '18px' }}>
          <SectionLabel style={{ marginBottom: '12px' }}>Preference Satisfaction Breakdown</SectionLabel>
          {satisfactionBreakdown.length === 0 ? <div style={{ fontSize: '12px', color: C.mutedLt }}>No assignments yet.</div> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={satisfactionBreakdown} margin={{ left: -18, right: 8, top: 8, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: C.muted }} axisLine={{ stroke: C.border }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: C.navy, border: 'none', borderRadius: '4px', fontSize: '12px', color: C.white }} cursor={{ fill: 'rgba(10,22,40,0.04)' }} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={44}>
                  {satisfactionBreakdown.map((d, i) => <Cell key={i} fill={d.name === 'Choice 1' ? C.gold : d.name === 'Alt' ? C.mutedLt : C.navy} />)}
                  <LabelList dataKey="count" position="top" style={{ fontSize: 10, fill: C.muted }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Session fill rate */}
      <div style={{ ...card, padding: '18px', marginBottom: '16px' }}>
        <SectionLabel style={{ marginBottom: '12px' }}>Session Fill Rate</SectionLabel>
        {fillRates.length === 0 ? <div style={{ fontSize: '12px', color: C.mutedLt }}>No slots.</div> : fillRates.map(f => (
          <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ width: '180px', fontSize: '12px', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            <div style={{ flex: 1, height: '12px', background: C.surface, borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, f.pct)}%`, height: '100%', background: f.pct > 90 ? C.danger : f.pct > 50 ? C.navy : C.mutedLt }} />
            </div>
            <span style={{ fontSize: '11.5px', color: C.muted, fontWeight: 600, width: '40px', textAlign: 'right' }}>{f.pct}%</span>
          </div>
        ))}
      </div>

      <PipelineTracking event={event} schedule={data!.schedule} slots={data!.slots} />
    </div>
  );
}

// Pipeline connection tracking: investors assigned to a manager → connected on
// AltConnect → pipeline stage.
function PipelineTracking({ event, schedule, slots }: { event: EventRow; schedule: InvestorSchedule[]; slots: ManagerSlot[] }) {
  const [rows, setRows] = useState<{ investorFirm: string; managerFirm: string; connected: boolean; stage: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const slotFirm: Record<string, string | null> = {};
        slots.forEach(s => { slotFirm[s.id] = s.firm_id; });
        // unique (investor firm, manager firm) pairs
        const pairs = new Map<string, { investorFirm: string | null; managerFirm: string | null }>();
        schedule.forEach(s => {
          const mf = s.manager_slot_id ? slotFirm[s.manager_slot_id] : null;
          if (s.firm_id && mf) pairs.set(`${s.firm_id}:${mf}`, { investorFirm: s.firm_id, managerFirm: mf });
        });
        const firmIds = Array.from(new Set([...pairs.values()].flatMap(p => [p.investorFirm, p.managerFirm]).filter(Boolean))) as string[];
        const { data: firms } = firmIds.length ? await supabase.from('firms').select('id,name').in('id', firmIds) : { data: [] as any[] };
        const nameById: Record<string, string> = {};
        (firms || []).forEach((f: any) => { nameById[f.id] = f.name; });

        const { data: conns } = await supabase.from('connections').select('id, allocator_firm_id, manager_firm_id, status');
        const connByPair: Record<string, { id: string; status: string }> = {};
        (conns || []).forEach((c: any) => { connByPair[`${c.allocator_firm_id}:${c.manager_firm_id}`] = { id: c.id, status: c.status }; });

        const connIds = Object.values(connByPair).map(c => c.id);
        const { data: stages } = connIds.length ? await supabase.from('pipeline_stages').select('connection_id, stage, created_at').in('connection_id', connIds).order('created_at', { ascending: false }) : { data: [] as any[] };
        const latestStage: Record<string, string> = {};
        (stages || []).forEach((st: any) => { if (!latestStage[st.connection_id]) latestStage[st.connection_id] = st.stage; });

        const result = [...pairs.values()].map(p => {
          const conn = connByPair[`${p.investorFirm}:${p.managerFirm}`];
          return {
            investorFirm: nameById[p.investorFirm!] || '-',
            managerFirm: nameById[p.managerFirm!] || '-',
            connected: !!conn && conn.status === 'approved',
            stage: conn ? (latestStage[conn.id] || 'Connected') : '-',
          };
        });
        setRows(result);
      } catch { setRows([]); }
      setLoading(false);
    })();
  }, [schedule, slots]);

  return (
    <div style={{ ...card, padding: '18px' }}>
      <SectionLabel style={{ marginBottom: '12px' }}>Pipeline Connection Tracking</SectionLabel>
      <div style={{ fontSize: '11.5px', color: C.muted, marginBottom: '10px' }}>Investors assigned to managers at this event who have since connected on AltConnect.</div>
      {loading ? <div style={{ fontSize: '12px', color: C.mutedLt }}>Loading…</div> :
        rows.length === 0 ? <div style={{ fontSize: '12px', color: C.mutedLt }}>No assignment-to-connection data yet.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
            <thead><tr style={{ background: C.surface }}>{['Investor Firm', 'Manager Firm', 'Connected', 'Pipeline Stage'].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.text }}>{r.investorFirm}</td>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.text }}>{r.managerFirm}</td>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}` }}>{r.connected ? <Pill text="Yes" color={C.success} bg="#F0FDF4" bd="#BBF7D0" /> : <Pill text="No" color={C.muted} bg={C.surface} bd={C.border} />}</td>
                  <td style={{ padding: '9px 14px', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12px', color: C.textSec, textTransform: 'capitalize' }}>{r.stage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
